import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { measureRenderedImageBox } from '@safetyhub/shared';
import type { BoundingBox, InspectionFinding, InspectionPhoto } from '../../../lib/inspections';
import { FindingsPanel, HazardEditState } from './FindingsPanel';
import { InteractiveHazardEditor } from './InteractiveHazardEditor';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from '../../icons';

interface PhotoWithFindings {
  photo: InspectionPhoto;
  findings: InspectionFinding[];
}

interface PhotoViewerWithEditorProps {
  /** 사진과 발견사항 목록 */
  photos: PhotoWithFindings[];
  /** 현재 선택된 사진 인덱스 */
  currentIndex: number;
  /** 열림 상태 */
  isOpen: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 인덱스 변경 핸들러 */
  onIndexChange?: (index: number) => void;
  /** 위험 영역 변경 핸들러 */
  onHazardChange?: (findingId: string, boundingBox: BoundingBox | null) => void;
  /** 위험 영역 편집 상태 */
  hazardEdits?: HazardEditState[];
  /** 위험 영역 되돌리기 핸들러 */
  onHazardRestore?: (findingId: string) => void;
}

/**
 * 측정 불가일 때의 표시 정보 — **0이어야 한다.** 오버레이는 `imageDisplayInfo.width > 0`일 때만
 * 렌더되므로 0은 「아무것도 그리지 않는다」가 되고, 그것이 측정 전에 박스를 엉뚱한 자리에 얹는
 * 것보다 낫다(공용 유틸이 `null`을 돌려주는 이유와 같은 판단).
 *
 * ## ⚠️ 그런데 그 약속은 **오늘 보장되지 않는다** — 「구조적으로 불가능」으로 읽지 말 것
 *
 * 「측정 불가 ⟹ 0」이 참이 되는 경로는 하나뿐이다 — `measureRenderedImageBox`가 `null`을 준 분기.
 * 측정 불가의 **다른 두 경로**는 이 값을 쓰지 않는다:
 *   ① `updateImageDisplayInfo`의 조기 반환(`!image.naturalWidth` 등)은 **옛 값을 그대로 남긴다.**
 *   ② 사진 이동(`index` 변경) 시 **아무도 리셋하지 않는다** — `<img>`에 `key`가 없어 요소가
 *      재생성되지 않고 `src`만 갈리므로, 새 `onLoad`가 오기 전까지 **직전 사진의 박스**가 남는다.
 *      종횡비가 다른 사진으로 넘기면 그 창에 오버레이가 **엉뚱한 자리**를 가리킨다.
 *
 * ## 왜 지금 발동하지 않나 — 근거는 하나뿐이고, 그것은 이 파일 밖에 있다
 *
 * **이 컴포넌트가 오늘 열리지 않기 때문**이다. 게이트는 호출부의 `hazardEditorState.isOpen`이고,
 * `setHazardEditorState` 호출부는 세 레이아웃에 **9곳**(선언 + 닫기 + `currentIndex`)뿐이며
 * **그 상태를 열림으로 세팅하는 자리가 0건**이다(2026-10-08 전수 · 2026-10-18 재측정 동일).
 *
 * ⚠️ **이 주석에 열림 상태의 «리터럴 표기»를 쓰지 말 것** — 그 표기가 census needle이라, 적으면
 * 그 축을 세는 사람이 **0 대신 이 주석을 센다**(2026-10-18 실제로 그렇게 1건이 잡혔고, 종전 서술은
 * 이 블록에만 그 리터럴을 **4번** 담고 있었다). 세는 법: `setHazardEditorState`를 **파일 루프**로
 * 모아 그 중 열림 세팅이 있는지 보고, **[대조군]**으로 `setPhotoViewerState`를 같은 방법으로 센다
 * 판정 조건은 **「열림 세팅이 0건」 ∧ 「대조군이 0건이 아니다」**이고 **두 수가 같은 것이 아니다.**
 * ⚠️ 2026-10-18에 여기 「9 ↔ 대조군 **9**」라 적었더니 **2026-10-20에 대조군이 18**이 되어 그 수가
 * 낡았다(조사원 `ws-recheck-photo` 적발) — 하필 **아래 ⚠️가 「절대 개수는 쓰지 않는다」고 적는
 * 자리에서** 그랬다. ⇒ **수를 고치지 않고 뺐다**: 대조군의 역할은 「계측기가 살아 있다」를
 * 보이는 것이므로 **0이 아니기만 하면 된다**.
 *
 * ⚠️ **숫자를 인용할 때 「무엇을 셌나」를 함께 읽어라 — 그게 없으면 두 수가 모순으로 보인다.**
 * 같은 축을 두 방법으로 셌고 **둘 다 맞다**: 「`setPhotoViewerState(` **전체 호출**」과
 * 「그중 **열림을 세팅하는** 호출」은 **다른 질문에 답한다.** ⚠️ 종전에 적혀 있던 「레포 전체 21건」류
 * **절대 개수는 쓰지 않는다** — 같은 needle이 2026-10-18에 **30**이고 그중 **4가 이 주석 자신**이었다.
 *
 * ⚠️ **그 사실이 이 서술의 유일한 근거다 — 「도달 불가」가 아니라 「빈도 0」이다.** 입구를
 * 배선하는 날(그 축은 별 항목이 소유한다) **위 ①②를 함께 고쳐야 한다**: 조기 반환을
 * `ZERO_DISPLAY_INFO`로 떨어뜨리고, `<img>`에 사진 id를 `key`로 주거나 `index` 변경 시 리셋한다.
 * 그때까지 이 상수의 JSDoc을 「측정 불가일 때는 0이다」로 되돌리지 말 것 — 그 문장을 믿고
 * 하류에서 `width > 0`을 「측정됐다」로 쓰면 그 창에 거짓이 된다.
 */
const ZERO_DISPLAY_INFO = { width: 0, height: 0, offsetX: 0, offsetY: 0 };

/**
 * 위험 영역 편집 기능이 포함된 사진 뷰어
 *
 * - 좌우 화살표로 사진 이동 (썸네일 없음)
 * - 위험 영역 드래그/리사이즈/삭제
 * - 발견사항 패널 (하단)
 * - 보고서 표시 옵션
 */
export function PhotoViewerWithEditor({
  photos,
  currentIndex,
  isOpen,
  onClose,
  onIndexChange,
  onHazardChange,
  hazardEdits = [],
  onHazardRestore,
}: PhotoViewerWithEditorProps) {
  const [index, setIndex] = useState(currentIndex);
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // 이미지 표시 정보
  const [imageDisplayInfo, setImageDisplayInfo] = useState(ZERO_DISPLAY_INFO);

  // 외부 인덱스 변경 동기화
  useEffect(() => {
    setIndex(currentIndex);
    setSelectedFindingId(null);
  }, [currentIndex]);

  // 모달 열릴 때 배경 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // ESC 키로 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          if (isZoomed) {
            setIsZoomed(false);
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          if (!isZoomed) goToPrevious();
          break;
        case 'ArrowRight':
          if (!isZoomed) goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, index, isZoomed]);

  /**
   * 이미지 로드 시 실제 표시 영역 계산 — **수식은 이 파일이 아니라 shared
   * `measureRenderedImageBox`가 소유한다**(`packages/shared/src/utils/analysis-region-render.ts`,
   * 값 spec 보유). 종전에는 `AnalysisRegionEditor`·`PhotoCardVertical`과 같은 수식이 세 벌
   * 있었다(2026-10-08 P4-l).
   *
   * ⚠️ **이 자리의 거동 spec은 원리적으로 불가능하다** — 아래 오버레이는
   * `imageDisplayInfo.width > 0`일 때만 렌더되고 그 값은 `getBoundingClientRect()`·`naturalWidth`
   * 에서 나오는데 **jsdom은 레이아웃을 계산하지 않아 그 둘이 늘 0**이다. 즉 이 변환이 틀려도
   * 깨지는 테스트가 없다. 기계로 잴 수 있는 것은 **값 층**(그 함수의 짝 spec)뿐이고, 「박스가
   * 사진 그 자리에 얹히는가」의 판정은 **브라우저 실측**이다(선례:
   * `docs/verification/2026-10-08-bbox-browser-measure/`). 여기를 바꾸면 거기서 다시 재라 —
   * 초록을 근거로 쓰지 말 것. 「rect로 재는가」 축만 배선 래칫이 지킨다
   * (`analysis-region/__tests__/bboxMeasureWiring.test.ts`).
   *
   * ## ⚠️ 재는 것은 `<img>`가 아니라 «컨테이너»다 — 그리고 두 상자는 «같지 않다»
   *
   * 공용 함수는 「이미지 **요소**의 렌더 크기」를 요구하는데 여기서는 컨테이너를 넘긴다. 편집기
   * (`AnalysisRegionEditor`)는 `<img>`가 `absolute inset-0 w-full h-full`이라 **두 상자가 같아서**
   * 괜찮은 자리지만 **이 자리는 다르다**: 컨테이너가 `flex items-center justify-center`이고
   * `<img>`가 `max-h-full max-w-full object-contain`이라 **img는 contain 크기로 줄어들어 가운데
   * 앉는다**(종횡비가 다르면 컨테이너보다 작다).
   *
   * 컨테이너를 넘기는 것 자체는 맞는 선택이다 — 오버레이가 그 컨테이너를 기준(`relative`)으로
   * 얹히므로 **그 좌표계의 값**이 필요하다. ⚠️ 이 입력을 `imageRef`의 rect로 「정리」하면 레터박스
   * offset이 0이 되어 박스가 통째로 밀린다. 두 자리가 같은 함수를 쓰면서 넘기는 상자가 다른 것은
   * 실수가 아니다.
   *
   * ## ⚠️ 그런데 그 결과가 img 자리와 일치하는 것은 **contain 배율이 1 이하일 때뿐**이다
   *
   * `max-h-full max-w-full`은 **상한만** 정하므로 사진이 컨테이너보다 작으면 img는 **natural
   * 크기 그대로** 그려진다 — `max-*`는 **업스케일하지 않는다.** 반면
   * `measureRenderedImageBox(..., 'contain')`은 `object-contain` 의미대로 **요소 상자를 채우도록
   * 확대**한다(`scale = min(scaleX, scaleY)`에 1 상한이 없다). 그래서:
   *
   * | 사진 | 컨테이너 | 실제 img | 이 계산 | |
   * |---|---|---|---|---|
   * | 2000×300 | 1000×800 | 1000×150 @(0,325) | 1000×150 @(0,325) | ✅ 일치(배율 0.5) |
   * | 400×300 | 1000×800 | **400×300 @(300,250)** | **1000×750 @(0,25)** | ❌ 어긋남(배율 2.5) |
   *
   * ⇒ **조건은 하나다: `min(scaleX, scaleY) > 1`이면 오버레이가 어긋난다.** 실사용 사진은 대개
   * 컨테이너보다 커서 드러나지 않지만 작은 이미지에서는 틀린다. **독립 근거가 옆에 있다** —
   * `PhotoViewer.tsx`의 어노테이션 캔버스가 **같은 CSS 조합**을 모델링하면서
   * `Math.min(maxWidth, naturalWidth)`로 **natural에 clamp**한다(= 업스케일 금지). 즉 이 축을
   * 그쪽은 다뤘고 여기는 안 다뤘다.
   *
   * ⚠️ **지금 고치지 않는다 — 이 컴포넌트가 열리지 않기 때문이다**(아래 `ZERO_DISPLAY_INFO`
   * JSDoc이 그 근거를 숫자로 적는다). 입구를 배선하는 날 **그 상수의 ①② 와 함께** 이것도 고쳐야
   * 한다. 처방은 `scale`에 1 상한을 두는 것(`objectFit`에 「업스케일 안 하는 contain」 축을
   * 추가하거나, 여기서 img rect와 컨테이너 offset을 각각 재서 합치는 것)이고, 어느 쪽이든
   * **공용 함수의 계약 변경**이라 팀장 판단이 필요하다.
   *
   * ⚠️ CSS 두 줄에 더 묶여 있다(타입도 spec도 못 본다 — jsdom은 레이아웃이 없다):
   *   ① 컨테이너의 `items-center justify-center` — 가운데 정렬이 아니면 offset 식이 거짓이 된다.
   *   ② 아래 오버레이 게이트의 **`!isZoomed`** — 줌의 `scale-150`은 **transform이라 img rect에는
   *      반영되고 컨테이너 rect에는 안 된다.** 지금은 줌 중에 오버레이를 안 그려서 무해하지만,
   *      그 게이트를 풀면 **이 측정이 1.5배만큼 거짓**이 된다.
   */
  const updateImageDisplayInfo = useCallback(() => {
    const container = containerRef.current;
    const image = imageRef.current;

    if (!container || !image || !image.naturalWidth || !image.naturalHeight) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const box = measureRenderedImageBox({
      elementWidth: containerRect.width,
      elementHeight: containerRect.height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      objectFit: 'contain',
    });

    if (!box) {
      // 컨테이너가 아직(또는 닫힘 전환 중) 0×0 — 종전 수식도 이 입력에서 0을 내놓아 오버레이가
      // 사라졌다. 낡은 박스를 남기면 0×0 상자 밖으로 삐져나와 엉뚱한 자리를 가리킨다.
      setImageDisplayInfo(prev =>
        prev.width === 0 && prev.height === 0 ? prev : ZERO_DISPLAY_INFO,
      );
      return;
    }

    setImageDisplayInfo({
      width: box.width,
      height: box.height,
      offsetX: box.left,
      offsetY: box.top,
    });
  }, []);

  // 리사이즈 감지
  useEffect(() => {
    if (!isOpen) return;

    const observer = new ResizeObserver(updateImageDisplayInfo);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isOpen, updateImageDisplayInfo]);

  const goToPrevious = useCallback(() => {
    if (index > 0) {
      const newIndex = index - 1;
      setIndex(newIndex);
      setSelectedFindingId(null);
      onIndexChange?.(newIndex);
    }
  }, [index, onIndexChange]);

  const goToNext = useCallback(() => {
    if (index < photos.length - 1) {
      const newIndex = index + 1;
      setIndex(newIndex);
      setSelectedFindingId(null);
      onIndexChange?.(newIndex);
    }
  }, [index, photos.length, onIndexChange]);

  const handleZoomToggle = useCallback(() => {
    setIsZoomed((prev) => !prev);
  }, []);

  // 현재 사진의 발견사항별 위험 영역 데이터 생성
  const currentPhotoData = photos[index];
  const currentPhoto = currentPhotoData?.photo;
  const currentFindings = currentPhotoData?.findings || [];

  // 편집 상태 맵
  const editStateMap = useMemo(
    () => new Map(hazardEdits.map((e) => [e.findingId, e])),
    [hazardEdits]
  );

  // 활성 발견사항 (삭제되지 않은 것)
  const activeFindings = useMemo(
    () => currentFindings.filter((f) => !f.deletedAt),
    [currentFindings]
  );

  // 위험 영역 데이터 (편집 상태 반영)
  const hazardRegions = useMemo(() => {
    return activeFindings
      .map((finding, idx) => {
        const editState = editStateMap.get(finding.id);

        // 삭제된 경우 제외
        if (editState?.isDeleted) return null;

        // 현재 boundingBox (편집 상태 우선)
        const boundingBox =
          editState?.current ||
          finding.aiAnalysisDetail?.boundingBox ||
          null;

        if (!boundingBox) return null;

        return {
          id: finding.id,
          number: idx + 1,
          boundingBox,
          aiLikelihoodScore: finding.aiLikelihoodScore ?? finding.aiAnalysisDetail?.riskAssessment?.likelihood ?? null,
          aiSeverityScore: finding.aiSeverityScore ?? finding.aiAnalysisDetail?.riskAssessment?.severity ?? null,
          aiRiskLevel: finding.aiAnalysisDetail?.riskAssessment?.riskLevel ?? null,
        };
      })
      .filter((h): h is NonNullable<typeof h> => h !== null);
  }, [activeFindings, editStateMap]);

  // 발견사항 패널용 편집 상태
  const findingHazardEdits = useMemo(() => {
    return activeFindings.map((finding) => {
      const editState = editStateMap.get(finding.id);
      const originalBox = finding.aiAnalysisDetail?.boundingBox || null;

      return {
        findingId: finding.id,
        original: originalBox,
        current: editState?.current || originalBox,
        isDeleted: editState?.isDeleted || false,
      };
    });
  }, [activeFindings, editStateMap]);

  // 위험 영역 변경 핸들러
  const handleHazardChange = useCallback(
    (hazardId: string, boundingBox: BoundingBox) => {
      onHazardChange?.(hazardId, boundingBox);
    },
    [onHazardChange]
  );

  // 위험 영역 삭제 핸들러
  const handleHazardDelete = useCallback(
    (hazardId: string) => {
      onHazardChange?.(hazardId, null);
      setSelectedFindingId(null);
    },
    [onHazardChange]
  );

  // 발견사항 선택 핸들러
  const handleFindingSelect = useCallback((findingId: string | null) => {
    setSelectedFindingId(findingId);
  }, []);

  if (!isOpen || photos.length === 0 || !currentPhoto) return null;

  const hasPrevious = index > 0;
  const hasNext = index < photos.length - 1;

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 z-20">
        <div className="flex items-center gap-4">
          {/* 닫기 버튼 */}
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:text-ink-300 p-2 -ml-2"
          >
            <XIcon className="h-6 w-6" />
          </button>

          {/* 페이지 표시 */}
          <span className="text-white text-sm">
            {index + 1} / {photos.length}
          </span>

          {/* 편집 모드 힌트 */}
          <span className="text-white/60 text-xs hidden sm:inline">
            위험 영역을 드래그하여 편집
          </span>
        </div>

        {/* 액션 버튼 */}
        <div className="flex items-center gap-2">
          {/* 줌 토글 */}
          <button
            type="button"
            onClick={handleZoomToggle}
            className={`p-2 transition-colors ${
              isZoomed
                ? 'text-brand-400 hover:text-brand-300'
                : 'text-white hover:text-ink-300'
            }`}
            title={isZoomed ? '축소 (ESC)' : '확대'}
          >
            <ZoomIcon isZoomed={isZoomed} />
          </button>
        </div>
      </div>

      {/* 사진 영역 */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden relative"
      >
        {/* 이전 버튼 */}
        {hasPrevious && !isZoomed && (
          <button
            type="button"
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-30"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
        )}

        {/* 사진 */}
        <img
          ref={imageRef}
          src={currentPhoto.url}
          alt="점검 사진"
          draggable={false}
          className={`
            max-h-full max-w-full object-contain select-none
            ${isZoomed ? 'scale-150' : ''}
          `}
          onLoad={updateImageDisplayInfo}
        />

        {/* 위험 영역 에디터 (줌 안 됐을 때만) */}
        {!isZoomed && imageDisplayInfo.width > 0 && (
          <InteractiveHazardEditor
            hazards={hazardRegions}
            containerWidth={imageDisplayInfo.width}
            containerHeight={imageDisplayInfo.height}
            offsetX={imageDisplayInfo.offsetX}
            offsetY={imageDisplayInfo.offsetY}
            selectedHazardId={selectedFindingId}
            onHazardSelect={handleFindingSelect}
            onHazardChange={handleHazardChange}
            onHazardDelete={handleHazardDelete}
          />
        )}

        {/* 다음 버튼 */}
        {hasNext && !isZoomed && (
          <button
            type="button"
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-30"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* 발견사항 패널 (하단) */}
      {!isZoomed && (
        <div className="bg-white border-t border-line max-h-[40vh] overflow-hidden flex flex-col">
          <FindingsPanel
            findings={activeFindings}
            selectedFindingId={selectedFindingId}
            onFindingSelect={handleFindingSelect}
            hazardEdits={findingHazardEdits}
            onHazardRestore={onHazardRestore}
          />
        </div>
      )}
    </div>
  );
}

// SVG Icons
function ZoomIcon({ isZoomed }: { isZoomed: boolean }) {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
      {isZoomed ? (
        <path d="M8 11h6" />
      ) : (
        <>
          <path d="M11 8v6" />
          <path d="M8 11h6" />
        </>
      )}
    </svg>
  );
}

export default PhotoViewerWithEditor;
